import React, { useEffect, useRef, useState } from "react";
import usePDA1072_R01Store from "./usePDA1072_R01Store";
import { usePDA1072_R01Content } from "./PDA1072_R01Content";

const PDA1072_R01DetailInfo: React.FC = () => {
  const { workOrderDetail, lotNo, setLotNo, scannedBomLot, selectedBomLots } =
    usePDA1072_R01Store();
  const { handleScanLot, handleSaveAll, handleCancel } = usePDA1072_R01Content();

  const lotInputRef = useRef<HTMLInputElement>(null);
  const [shipQty, setShipQty] = useState<string>("");
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);

  useEffect(() => {
    if (workOrderDetail && lotInputRef.current) {
      lotInputRef.current.focus();
    }
  }, [workOrderDetail]);

  // LOT 전량 이동만 허용 → 출고수량 = lot_qty 전량
  useEffect(() => {
    if (scannedBomLot?.lotQty != null) {
      setShipQty(String(Number(scannedBomLot.lotQty)));
    } else {
      setShipQty("");
    }
  }, [scannedBomLot]);

  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapTime < 300) {
      setKeyboardEnabled(true);
      lotInputRef.current?.focus();
    }
    setLastTapTime(now);
  };

  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) setTimeout(() => lotInputRef.current?.focus(), 100);
  };

  const handleLotNoKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && lotNo.trim()) handleScanLot();
  };

  if (!workOrderDetail) {
    return (
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="text-center text-gray-500 py-8">작업지시를 선택하세요</div>
      </div>
    );
  }

  const header = workOrderDetail.header;

  return (
    <div className="p-4 bg-white border-b border-gray-200">
      <div className="space-y-1">
        {/* 1. 지시번호 */}
        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">지시번호</label>
          <input
            type="text"
            value={header?.productOrderNo ?? ""}
            disabled
            className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
          />
        </div>

        {/* 2. Lot.No (스캔) */}
        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">Lot.No</label>
          <div className="flex-1 flex gap-1">
            <input
              ref={lotInputRef}
              type="text"
              value={lotNo}
              onChange={(e) => setLotNo(e.target.value)}
              onKeyDown={handleLotNoKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              placeholder="LOT 스캔 후 Enter"
              autoComplete="off"
              className="flex-1 px-2 max-w-[195px] text-lg border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                keyboardEnabled ? "bg-blue-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* 3. 품명 */}
        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">품명</label>
          <input
            type="text"
            value={scannedBomLot?.itemNm ?? ""}
            disabled
            className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
          />
        </div>

        {/* 4. 규격 */}
        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">규격</label>
          <input
            type="text"
            value={scannedBomLot?.itemSpec ?? ""}
            disabled
            className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
          />
        </div>

        {/* 5. Lot 재고 */}
        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">Lot 재고</label>
          <input
            type="text"
            value={
              scannedBomLot?.lotQty != null
                ? `${Number(scannedBomLot.lotQty).toLocaleString()} ea`
                : ""
            }
            disabled
            className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
          />
        </div>

        {/* 6. 출고수량 */}
        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">출고수량</label>
          <input
            type="text"
            value={shipQty}
            readOnly
            disabled
            className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
          />
        </div>

        {/* 7. 버튼 */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleCancel}
            className="flex-1 py-1 text-lg bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            취소
          </button>
          <button
            onClick={handleSaveAll}
            disabled={selectedBomLots.length === 0}
            className="flex-1 py-1 text-lg bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {selectedBomLots.length > 0 ? `등록(${selectedBomLots.length})` : "등록"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDA1072_R01DetailInfo;
